import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Library,
  Presentation,
  FileDown,
  Share2,
  Palette,
  HeartHandshake,
  Building2,
  GraduationCap,
  Landmark,
  Users,
} from 'lucide-react'
import {
  Container,
  SectionLabel,
  SectionHeading,
  FeatureCard,
  CheckList,
  CtaBand,
  PrimaryButton,
  SecondaryButton,
  ease,
} from '@/components/umbrella/ui'

export const Route = createFileRoute('/solutions/community-partners')({
  component: CommunityPartnersPage,
})

const WHO = [
  { Icon: Library, label: 'Libraries' },
  { Icon: HeartHandshake, label: 'Nonprofits' },
  { Icon: Users, label: 'Senior centers' },
  { Icon: Building2, label: 'Employers' },
  { Icon: GraduationCap, label: 'Schools & colleges' },
  { Icon: Landmark, label: 'Faith communities' },
]

const FEATURES = [
  {
    Icon: Share2,
    title: 'Ready-to-share education',
    body: 'A complete scam-prevention site you can link to from day one, with no content to write and nothing to maintain.',
  },
  {
    Icon: Presentation,
    title: 'Workshop kits',
    body: 'Run a session in your space with guided material designed for mixed levels of tech confidence.',
  },
  {
    Icon: FileDown,
    title: 'Printable handouts',
    body: 'One-page guides and checklists for members who prefer paper, ready to print or hand out at events.',
  },
  {
    Icon: Palette,
    title: 'Co-branded to your organization',
    body: 'Add your name and logo so the resource feels like it comes from a group your community already trusts.',
  },
]

/** Centered hero with the audience chips folded in, so "who this is for"
 *  reads in the first screen instead of as its own sparse section. */
function CommunityHero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-100">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,rgba(37,99,235,0.07),transparent_60%)]" />
      <Container className="py-20 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="mx-auto max-w-3xl text-center"
        >
          <SectionLabel>For Community Partners</SectionLabel>
          <h1 className="mt-4 text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
            Help your community{' '}
            <span className="text-blue-600">see scams coming.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
            The groups people already trust are the best messengers for scam
            prevention. ScamDefense gives you ready-to-share education, workshop
            kits, and printed materials, so you can protect the people you serve
            without becoming fraud experts yourselves.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <PrimaryButton label="Talk to our team" href="#contact" />
            <SecondaryButton label="See the platform" href="/platform" />
          </div>
          <div className="mx-auto mt-10 flex max-w-2xl flex-wrap items-center justify-center gap-2.5">
            {WHO.map(w => (
              <span
                key={w.label}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
              >
                <w.Icon className="h-4 w-4 text-blue-600" />
                {w.label}
              </span>
            ))}
          </div>
        </motion.div>
      </Container>
    </section>
  )
}

function CommunityPartnersPage() {
  return (
    <div className="bg-white">
      <CommunityHero />

      <Container className="py-20">
        <SectionHeading
          label="What you get"
          title="Everything you need to start protecting people"
          blurb="Practical tools you can put to work this week, in your organization’s name."
          wide
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {FEATURES.map(f => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </Container>

      <section className="bg-slate-50 py-20">
        <Container className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              label="How it fits"
              title="Made for how community groups actually work"
              align="left"
            />
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              No budget line, no IT project, and no fraud expertise required.
              You bring the trust your community already has in you; we bring
              the program.
            </p>
          </div>
          <CheckList
            items={[
              'Link it from your website, newsletter, or notice board',
              'Run workshops with ready-made session kits',
              'Print handouts for members who prefer paper',
              'Your name and logo on everything you share',
              'No accounts or personal data required from your members',
              'Content kept current as scam tactics change',
            ]}
          />
        </Container>
      </section>

      <CtaBand
        title="Bring scam prevention to your community."
        blurb="Let’s talk about a ready-to-share ScamDefense deployment for your organization."
      />
    </div>
  )
}
